/** @jsxImportSource @emotion/react */
import { formatErrorResponse } from '@quickbit/backoffice-core-frontend';
import {
  Button,
  Card,
  Input,
  ListItem,
  Snackbar,
  Switch,
  Typography,
  UtilityButton,
  createStyles,
  useTheme,
} from '@quickbit/qb-design-system';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useAsync, useAsyncFn, useUpdateEffect } from 'react-use';
import { useOrganisation } from 'contexts';
import { getConfig, updateConfig } from '../api';
import { getMerchantLogo, postMerchantLogo } from './api';
import { MerchantLogo } from './components/MerchantLogo';
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'tiff', 'bmp'];
const logoTooltipMsg = `The logo image should be min 50px(width) X 50px(height) and max 1000px(width) X 1000px(height). 
Allowed file types are: .jpg, .jpeg, .png, .gif, .tiff and .bmp. 
Max file size allowed is 2MB.`;
type Alert = {
  type: 'success' | 'danger';
  show: boolean;
  message: string;
};

export const BrandingPage = () => {
  const [alert, setAlert] = useState<Alert>({
    type: 'success',
    show: false,
    message: '',
  });
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const { spacing, palette } = useTheme();
  const {
    isPlatform,
    selectedMerchant: { merchantId },
  } = useOrganisation();

  if (isPlatform) {
    throw new Error('Branding page is not available for platform');
  }

  const [selectedFile, setSelectedFile] = useState<Blob>();
  const [showImage, setShowImage] = useState<ArrayBuffer | string | null>(null);
  const [fileName, setFileName] = useState('');
  const [showLogo, setShowLogo] = useState(false);
  const [msg, setMsg] = useState('');
  const [displayName, setDisplayName] = useState<string>('');
  const { loading } = useAsync(async () => {
    const config = await getConfig();
    setDisplayName(config.displayName);
  });
  const [{ loading: updateDisplayNameLoading }, updateDisplayName] =
    useAsyncFn(async () => {
      const conf = await updateConfig({ displayName });
      return conf;
    }, [displayName]);

  const validateDisplayName = () => {
    if (!displayName.trim()) {
      return 'Display name cannot be blank';
    }
  };

  const [{ error: uploadLogoError, loading: uploadLogoLoading }, postLogoFn] =
    useAsyncFn(
      async (logoPayload: {
        imagedata?: string;
        filename?: string;
        enabled: boolean;
      }) => {
        const data = await postMerchantLogo(logoPayload);
        setAlert({
          show: true,
          message: 'Merchant logo updated successfully',
          type: 'success',
        });
        return data;
      }
    );
  useUpdateEffect(() => {
    if (uploadLogoError) {
      setAlert({
        show: true,
        message: formatErrorResponse(uploadLogoError),
        type: 'danger',
      });
    }
  }, [uploadLogoError]);

  const [{ value: getLogoValue }, getLogoFn] = useAsyncFn(async () => {
    const data = await getMerchantLogo();
    setShowImage(data.merchantLogo.logoUrl);
    setShowLogo(data.merchantLogo.enabled);
    return data.merchantLogo;
  });

  useUpdateEffect(() => {
    if (getLogoValue) {
      setShowImage(getLogoValue.logoUrl);
      setShowLogo(getLogoValue.enabled);
    }
  }, [getLogoValue]);

  useEffect(() => {
    void getLogoFn();
  }, [merchantId, getLogoFn]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    const file = event.target.files[0];
    const extension = file.type.split('/')[1];

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setMsg(
        'Invalid file type.Allowed file types are: .jpg, .jpeg, .png, .gif, .tiff and .bmp.'
      );
      return;
    }
    if (file.size > 2048000) {
      setMsg('File size is too big. Max size allowed is 2MB.');
      return;
    }
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const width = img.width;
      const height = img.height;
      const maxDimension = 1000;
      const minDimension = 50;

      if (
        !(minDimension <= width && width <= maxDimension) ||
        !(minDimension <= height && height <= maxDimension)
      ) {
        event.target.value = '';
        setMsg('Image does not conform to specfications.');
        setSelectedFile(undefined);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setShowImage(reader.result);
      };
      reader.readAsDataURL(file);

      setSelectedFile(file);
      setFileName(file.name);
    };
  };

  const handleSave = () => {
    if (selectedFile) {
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1];

        const payload = {
          imagedata: base64String,
          filename: fileName,
          enabled: showLogo,
        };
        void postLogoFn(payload);
      };
    } else {
      const payload = {
        enabled: showLogo,
      };
      void postLogoFn(payload);
    }
  };

  const handleFileSelect = () => {
    setMsg('');
    setSelectedFile(undefined);
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <>
      <Card>
        <Typography variant="subtitle">Branding</Typography>
      </Card>
      <div css={{ display: 'flex', marginTop: spacing.xs, gap: spacing.xs }}>
        <Card
          css={{
            display: 'flex',
            flexDirection: 'column',
            width: '50%',
          }}
        >
          <div css={{ display: 'flex', columnGap: spacing.xxxs }}>
            <Typography variant="subtitle">Merchant Logo</Typography>
          </div>
          <div
            css={{
              display: 'none',
              gridTemplateColumns: 'auto auto',
              alignItems: 'center',
              gap: spacing.s,
            }}
          >
            <input
              type="file"
              ref={inputRef}
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>
          {showImage && !msg && <MerchantLogo logoUrl={showImage as string} />}

          <div
            css={{
              marginTop: spacing.s,
              rowGap: spacing.s,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {msg && (
              <Typography variant="bodySmall" color={palette.danger.default}>
                {msg}
              </Typography>
            )}
            <div css={{ display: 'flex', alignItems: 'center' }}>
              <UtilityButton
                text={selectedFile ? 'Replace Logo' : 'Select Logo'}
                onClick={handleFileSelect}
                variant="primary"
              />
            </div>
            {msg && (
              <Snackbar
                message={logoTooltipMsg}
                variant={'primary'}
                showAsBanner={true}
              />
            )}
            <ListItem
              title="Display logo on checkout page"
              css={[styles.listItemBorder]}
              action={
                <Switch
                  checked={showLogo}
                  onChange={(checked) => {
                    setShowLogo(checked);
                  }}
                />
              }
            />
          </div>

          <div css={{ marginTop: spacing.s, columnGap: spacing.s }}>
            <Button
              text="Submit"
              onClick={handleSave}
              disabled={uploadLogoLoading || !showImage}
              loading={uploadLogoLoading}
              variant="primary"
            />
          </div>
        </Card>
        <Card
          css={{
            display: 'flex',
            flexDirection: 'column',
            width: '50%',
            rowGap: spacing.xs,
            justifyContent: 'space-between',
          }}
        >
          <div>
            <Typography variant="subtitle">Display Name</Typography>
            <Typography variant="bodySmall" color={palette.grey.x600}>
              Display name of the merchant to be shown to the end users.
            </Typography>
          </div>
          <Input
            error={!!validateDisplayName()}
            helperText={validateDisplayName()}
            label="Display Name"
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
            }}
          />
          <Button
            text="Submit"
            loading={loading || updateDisplayNameLoading}
            disabled={
              loading || updateDisplayNameLoading || !!validateDisplayName()
            }
            variant="primary"
            onClick={updateDisplayName}
          />
        </Card>
      </div>
      <Snackbar
        variant={alert.type}
        message={alert.message}
        onClose={() => {
          setAlert({ ...alert, show: false });
        }}
        open={alert.show}
      />
    </>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  listItemBorder: {
    borderRadius: spacing.xxxs,
    border: `1px solid ${palette.grey.x200}`,
    ':hover': {
      borderColor: palette.grey.x600,
    },
  },
}));
